import { breakpointNumbers } from '@ui/constants/design-tokens/breakpoints';
import { useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

type BreakpointNumbers = typeof breakpointNumbers;

export type BreakpointCheckerFn = (
  width: number,
  breakpointNumbers: BreakpointNumbers,
) => boolean;

export const useBreakpoint = (checkerFn: BreakpointCheckerFn) => {
  const [boolState, setBoolState] = useState(checkerFn(0, breakpointNumbers));
  // MUST be state: as a ref, consumers only saw `true` after some UNRELATED
  // re-render — when the breakpoint value didn't change on first measure
  // (always the case: checker(0) usually equals checker(realWidth) for one
  // of the branches) nothing re-rendered and gates like the trade chart's
  // `hasResizeTriggered` stayed false indefinitely (chart never mounted
  // until a price tick happened to re-render the page).
  const [hasResizeTriggered, setHasResizeTriggered] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const handleResize = () => {
      setHasResizeTriggered(true);
      setBoolState(checkerFn(window.innerWidth, breakpointNumbers));
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [checkerFn]);

  return {
    value: boolState,
    hasResizeTriggered,
  };
};

export const checkIsDesktopUp: BreakpointCheckerFn = (w, bp) => w >= bp.desktop;
